import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { useToast } from '@/components/ui'
import { projectService } from '@/services/http/projectService'

import type { ListPortfolioProject } from '@/services/http/projectService/project'
import type { QueryParams } from '@/utils/queryParams'

type UseGetKanbanProjectsProps = {
  columnId?: string
  query: QueryParams
  handleSetProjects?: (projects: ListPortfolioProject[]) => void
}

export const useGetKanbanProjects = ({
  columnId,
  query,
  handleSetProjects,
}: UseGetKanbanProjectsProps) => {
  const { limit = 10, page = 1, ...rest } = query
  const { toast } = useToast()

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch: refetchPortfolioProject,
  } = useInfiniteQuery({
    queryKey: [
      'portfolioProject',
      columnId,
      {
        limit,
        ...rest,
      },
    ],
    queryFn: async ({ pageParam = page }) => {
      const response = await projectService.project.get({
        query: {
          ...(columnId && { portfolioColumnId: columnId }),
          limit,
          page: pageParam,
          ...rest,
        },
      })

      if (handleSetProjects) {
        handleSetProjects(response.projects)
      }

      return response
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.paginate.page < lastPage.paginate.pages) {
        return lastPage.paginate.page + 1
      }
      return undefined
    },
    initialPageParam: page,
  })

  useEffect(() => {
    if (isError)
      toast({
        title: 'Erro ao carregar o projeto do portfólio',
        variant: 'destructive',
      })
  }, [isError, toast])

  const projects = data?.pages.flatMap((page) => page.projects) ?? []

  return {
    projects,
    isLoading,
    isLoadingNextPage: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetchPortfolioProject,
  }
}
